/**
 * Audio utilities for voice recording and processing
 */

export interface AudioConfig {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
}

export interface RecordingState {
  isRecording: boolean;
  duration: number;
  volume: number;
  error: string | null;
}

// Default audio configuration for high-quality recording
export const DEFAULT_AUDIO_CONFIG: AudioConfig = {
  sampleRate: 44100,
  channels: 1, // Mono for voice
  bitsPerSample: 16
};

// Audio constraints for getUserMedia
export const getAudioConstraints = (): MediaStreamConstraints => ({
  audio: {
    sampleRate: DEFAULT_AUDIO_CONFIG.sampleRate,
    channelCount: DEFAULT_AUDIO_CONFIG.channels,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
  video: false
});

// Convert audio blob to base64 for API transmission
export const audioToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix to get just the base64
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Calculate audio volume from audio data for visualization
export const calculateVolume = (audioData: Float32Array): number => {
  let sum = 0;
  for (let i = 0; i < audioData.length; i++) {
    sum += audioData[i] * audioData[i];
  }
  const rms = Math.sqrt(sum / audioData.length);
  return Math.min(1, rms * 10); // Scale and cap at 1
};

// Format duration for display (mm:ss)
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Check if browser supports audio recording
export const isAudioRecordingSupported = (): boolean => {
  return !!(navigator.mediaDevices && 
           navigator.mediaDevices.getUserMedia && 
           window.MediaRecorder &&
           MediaRecorder.isTypeSupported('audio/webm') || 
           MediaRecorder.isTypeSupported('audio/mp4'));
};

// Get supported audio MIME types
export const getSupportedAudioTypes = (): string[] => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/wav'
  ];
  
  return types.filter(type => MediaRecorder.isTypeSupported(type));
};

// Error messages for common audio issues
export const AUDIO_ERRORS = {
  NOT_SUPPORTED: 'Audio recording is not supported in this browser',
  PERMISSION_DENIED: 'Microphone permission was denied',
  NO_MICROPHONE: 'No microphone found',
  RECORDING_FAILED: 'Recording failed to start',
  PROCESSING_FAILED: 'Failed to process audio data'
} as const; 